'use client';

import { Box, Button } from '@mui/material';

import { useT } from '@/hooks';
import { SearchSectionHeader } from './SearchSectionHeader';

import { BROWSE_FILTERS, type BrowseFilter } from '@/constants/search.constants';

interface BrowseItemProps {
  filter: BrowseFilter;
  onClick: (f: BrowseFilter) => void;
}

const BrowseItem = ({ filter, onClick }: BrowseItemProps) => {
  const { t } = useT();
  const handleClick = () => onClick(filter);
  const Icon = filter.icon;

  return (
    <Button
      variant="outlined"
      startIcon={<Icon sx={{ fontSize: '1.1rem' }} />}
      onClick={handleClick}
      sx={{
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
        color: 'text.primary',
        textTransform: 'none',
        fontSize: '0.9rem',
        fontWeight: 600,
        py: 0.85,
        px: 2,
        borderRadius: '10px',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        },
        '& .MuiButton-startIcon': { color: 'text.secondary' },
      }}
    >
      {t(`search.${filter.label.toLowerCase()}`)}
    </Button>
  );
};

interface SearchBrowseResultsProps {
  onFilterClick: (filter: BrowseFilter) => void;
}

export const SearchBrowseResults = ({ onFilterClick }: SearchBrowseResultsProps) => {
  const { t } = useT();
  const renderBrowseItems = (filter: BrowseFilter) => (
    <BrowseItem key={filter.label} filter={filter} onClick={onFilterClick} />
  );

  const renderBrowseItemsList = BROWSE_FILTERS.map(renderBrowseItems);
  return (
    <Box sx={{ p: 2.5 }}>
      <SearchSectionHeader title={t('search.browse')} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>{renderBrowseItemsList}</Box>
    </Box>
  );
};
